import React, { useEffect, useMemo, useRef, useState } from "react";
import logo from "../../assets/dr-is-in.png";
import "./AdminAnalyticsDashboard.css";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  "https://doctorisin-genesis-1-0.onrender.com";

type AudienceScope = "all" | "students" | "parents" | "teachers" | "advisors";

type FilterOption = {
  value: string;
  label: string;
};

type PublicCounts = {
  students: number;
  advisors: number;
  organizations: number;
  parents: number;
  teachers: number;
  contacts: number;
  tipsTotal: number;
  tipsApproved: number;
  studentsOnline: number;
  advisorsOnline: number;
  flaggedInteractions: number;
  usageEvents: number;
  advisorRequests: number;
  translationSessions: number;
  closureEvents: number;
};

type RankRow = {
  key: string;
  label: string;
  count: number;
  pct: number;
};

type AuraFeedItem = {
  id?: number;
  created_at?: string;
  school_name?: string;
  category?: string;
  trigger?: string;
  severity?: string;
  audience?: string;
  signal_category?: string;
  signal_label?: string;
  user_role?: string;
  country?: string;
  region?: string;
  district?: string;
};

type AuraSummary = {
  ok?: boolean;
  active_signals?: number;
  flagged_schools?: number;
  wellness_engagement?: number;
  crisis_watch?: string;
  advisor_response?: number;
  score?: number;
  counts?: {
    students?: number;
    advisors?: number;
    doctors?: number;
  };
  online?: {
    students?: number;
    advisors?: number;
    doctors?: number;
  };
  totals?: {
    total?: number;
    critical?: number;
    high?: number;
    moderate?: number;
    low?: number;
    schools?: number;
  };
  schools?: Array<{ name?: string; total?: number }>;
};

type AdminSummary = {
  ok?: boolean;
  totals?: {
    students?: number;
    doctors?: number;
    organizations?: number;
    contacts?: number;
    parent_teachers?: number;
    support_signal_events?: number;
  };
};

function getAdminToken() {
  return (
    localStorage.getItem("admin_token") ||
    sessionStorage.getItem("admin_token") ||
    localStorage.getItem("admin_access_token") ||
    sessionStorage.getItem("admin_access_token") ||
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    ""
  );
}

function safeNum(v: any, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}

function cleanLabel(value: any) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const lowered = raw.toLowerCase();
  if (["unknown", "n/a", "null", "undefined", "none"].includes(lowered)) {
    return "Unknown";
  }
  return raw;
}

function pct(count: number, total: number) {
  if (!total) return 0;
  return Math.round((count / total) * 100);
}

function normalizeAudience(value: any) {
  const v = String(value || "").trim().toLowerCase();
  if (v.includes("student")) return "students";
  if (v.includes("advisor") || v.includes("doctor")) return "advisors";
  if (v.includes("parent")) return "parents";
  if (v.includes("teacher")) return "teachers";
  if (v === "pt") return "parents";
  return "all";
}

function inAudienceScope(item: AuraFeedItem, scope: AudienceScope) {
  if (scope === "all") return true;
  const role = normalizeAudience(item.audience || item.user_role);
  return role === scope;
}

function toKey(label: string) {
  return String(label || "unknown")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") || "unknown";
}

function formatCategory(value: any) {
  const raw = cleanLabel(value);
  if (!raw) return "General Support";

  const t = raw.toLowerCase();

  if (t.includes("digital influence") || t.includes("viral") || t.includes("tiktok") || t.includes("challenge")) {
    return "Digital Influence / Viral Challenge";
  }
  if (t.includes("medication misuse") || t.includes("cold medicine") || t.includes("benadryl") || t.includes("cough") || t.includes("medicine")) {
    return "Medication Misuse";
  }
  if (t.includes("sext") || t.includes("nude") || t.includes("image sharing") || t.includes("private image")) {
    return "Sexting / Image Sharing";
  }
  if (t.includes("groom") || t.includes("sextortion") || t.includes("predator")) {
    return "Grooming / Sextortion";
  }
  if (t.includes("bully") || t.includes("cyberbully") || t.includes("harassment")) {
    return "Bullying / Cyberbullying";
  }
  if (t.includes("suicide loss") || t.includes("community suicide") || t.includes("grief") || t.includes("memorial")) {
    return "Suicide Loss / Grief";
  }
  if (t.includes("self harm") || t.includes("self_harm") || t.includes("suicide concern") || t === "suicide") {
    return "Self-Harm / Suicide Risk";
  }
  if (t.includes("eating") || t.includes("diet pill") || t.includes("body image") || t.includes("purging") || t.includes("steroid")) {
    return "Eating Disorder / Body Image";
  }
  if (t.includes("substance") || t.includes("drug") || t.includes("vaping") || t.includes("alcohol")) {
    return "Substance Use";
  }
  if (t.includes("violence") || t.includes("weapon") || t.includes("shooting") || t.includes("active shooter")) {
    return "Violence / Weapon Threat";
  }
  if (t.includes("family")) {
    return "Family Stress";
  }
  if (t.includes("advisor")) {
    return "Advisor Request";
  }
  if (t.includes("closure") || t.includes("follow")) {
    return "Closure / Follow-Up";
  }
  if (t.includes("anxiety") || t.includes("panic")) {
    return "Anxiety / Panic";
  }
  if (t.includes("depression") || t.includes("hopeless")) {
    return "Depression / Hopelessness";
  }
  if (t.includes("burnout") || t.includes("overload")) {
    return "Teacher Burnout / Overload";
  }
  if (t.includes("general")) {
    return "General Support";
  }

  return raw
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function severityLabel(value: any) {
  const s = String(value || "").trim().toLowerCase();
  if (s === "critical") return "Critical";
  if (s === "high" || s === "elevated") return "High";
  if (s === "moderate" || s === "medium") return "Moderate";
  if (s === "low") return "Low";
  return "Unknown";
}

function rankRows(labels: string[], fallback?: RankRow[]) {
  const counts = new Map<string, number>();

  labels.forEach((label) => {
    const clean = cleanLabel(label);
    if (!clean) return;
    counts.set(clean, (counts.get(clean) || 0) + 1);
  });

  const total = Array.from(counts.values()).reduce((sum, n) => sum + n, 0);

  if (!total && fallback) return fallback;

  return Array.from(counts.entries())
    .map(([label, count]) => ({
      key: toKey(label),
      label,
      count,
      pct: pct(count, total),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

function asArray(value: any): any[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return Object.values(value);
  return [];
}

function collectPresenceItems(payload: any): any[] {
  const out: any[] = [];
  const seen = new Set<any>();

  function add(item: any) {
    if (!item || typeof item !== "object") return;
    if (seen.has(item)) return;
    seen.add(item);
    out.push(item);
  }

  function walk(value: any, depth = 0) {
    if (!value || depth > 3) return;
    if (Array.isArray(value)) {
      value.forEach((x) => walk(x, depth + 1));
      return;
    }
    if (typeof value !== "object") return;

    const looksLikePresence =
      value.role ||
      value.user_role ||
      value.audience ||
      value.doctor_id ||
      value.advisor_id ||
      value.student_pk ||
      value.student_id ||
      value.pt_id ||
      value.display_name ||
      value.name ||
      value.email;

    if (looksLikePresence) add(value);

    ["items", "presence", "connections", "online", "users", "students", "doctors", "advisors", "parents", "teachers"].forEach((k) => {
      if (value[k]) walk(value[k], depth + 1);
    });
  }

  walk(payload);
  return out;
}

function presenceKey(item: any, fallback: string) {
  return String(
    item?.doctor_id ||
      item?.advisor_id ||
      item?.student_pk ||
      item?.student_id ||
      item?.pt_id ||
      item?.user_id ||
      item?.id ||
      item?.email ||
      item?.display_name ||
      item?.name ||
      fallback
  );
}

function summarizeRealtimePresence(rawPresence: any, rawDoctors: any) {
  const presenceItems = collectPresenceItems(rawPresence);
  const doctorItems = [
    ...asArray(rawDoctors?.doctors),
    ...asArray(rawDoctors?.items),
    ...asArray(rawDoctors),
  ].filter((x) => x && typeof x === "object");

  const studentKeys = new Set<string>();
  const advisorKeys = new Set<string>();
  const gradeCounts = new Map<string, number>();
  const advisorProfessionCounts = new Map<string, number>();

  presenceItems.forEach((item, idx) => {
    const role = normalizeAudience(item?.role || item?.user_role || item?.audience || item?.type);

    if (role === "students" || item?.student_pk || item?.student_id) {
      const key = presenceKey(item, `student-${idx}`);
      if (!studentKeys.has(key)) {
        studentKeys.add(key);
        const grade = cleanLabel(item?.grade || item?.grade_level || item?.student_grade) || "Online";
        gradeCounts.set(grade, (gradeCounts.get(grade) || 0) + 1);
      }
    }

    if (role === "advisors" || item?.doctor_id || item?.advisor_id) {
      const key = presenceKey(item, `advisor-${idx}`);
      if (!advisorKeys.has(key)) {
        advisorKeys.add(key);
        const profession =
          cleanLabel(item?.specialties || item?.specialty || item?.medical_field || item?.profession || item?.status) ||
          "Advisor";
        advisorProfessionCounts.set(profession, (advisorProfessionCounts.get(profession) || 0) + 1);
      }
    }
  });

  doctorItems.forEach((item, idx) => {
    const online = Boolean(
      item?.is_online ||
        item?.online ||
        item?.is_available ||
        item?.availability ||
        String(item?.status || "").toLowerCase().includes("on call")
    );
    if (!online) return;

    const key = presenceKey(item, `doctor-${idx}`);
    if (!advisorKeys.has(key)) {
      advisorKeys.add(key);
      const profession = cleanLabel(item?.specialties || item?.medical_field || item?.profession) || "Advisor";
      advisorProfessionCounts.set(profession, (advisorProfessionCounts.get(profession) || 0) + 1);
    }
  });

  return {
    studentsOnline: studentKeys.size,
    advisorsOnline: advisorKeys.size,
    studentGroups: Array.from(gradeCounts.entries()).map(([key, count]) => ({ key, count })),
    advisorGroups: Array.from(advisorProfessionCounts.entries()).map(([key, count]) => ({ key, count })),
  };
}

function normalizeOptions(items: any[], valueKeys: string[], labelKeys: string[]): FilterOption[] {
  const seen = new Set<string>();
  const out: FilterOption[] = [];

  (Array.isArray(items) ? items : []).forEach((item: any) => {
    const value =
      valueKeys.map((k) => cleanLabel(item?.[k])).find(Boolean) ||
      labelKeys.map((k) => cleanLabel(item?.[k])).find(Boolean) ||
      cleanLabel(item);

    const label =
      labelKeys.map((k) => cleanLabel(item?.[k])).find(Boolean) ||
      value;

    if (!value || seen.has(value.toLowerCase())) return;
    seen.add(value.toLowerCase());
    out.push({ value, label });
  });

  return out.sort((a, b) => a.label.localeCompare(b.label));
}

function buildQuery(params: Record<string, any>) {
  const sp = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    const clean = String(value ?? "").trim();
    if (clean) sp.set(key, clean);
  });

  const q = sp.toString();
  return q ? `?${q}` : "";
}

async function apiFetch<T = any>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAdminToken();

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });

  const text = await res.text();
  let data: any = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }

  if (!res.ok) {
    throw new Error(
      typeof data?.detail === "string"
        ? data.detail
        : typeof data?.message === "string"
          ? data.message
          : `Request failed: ${res.status}`
    );
  }

  return data as T;
}

function categoryFallback(): RankRow[] {
  return [
    { key: "digital_influence_viral_challenge", label: "Digital Influence / Viral Challenge", count: 0, pct: 0 },
    { key: "medication_misuse", label: "Medication Misuse", count: 0, pct: 0 },
    { key: "sexting_image_sharing", label: "Sexting / Image Sharing", count: 0, pct: 0 },
    { key: "bullying_cyberbullying", label: "Bullying / Cyberbullying", count: 0, pct: 0 },
    { key: "suicide_loss_grief", label: "Suicide Loss / Grief", count: 0, pct: 0 },
    { key: "eating_disorder_body_image", label: "Eating Disorder / Body Image", count: 0, pct: 0 },
    { key: "self_harm_suicide_risk", label: "Self-Harm / Suicide Risk", count: 0, pct: 0 },
    { key: "family_stress", label: "Family Stress", count: 0, pct: 0 },
  ];
}

function severityFallback(): RankRow[] {
  return [
    { key: "low", label: "Low", count: 0, pct: 0 },
    { key: "moderate", label: "Moderate", count: 0, pct: 0 },
    { key: "high", label: "High", count: 0, pct: 0 },
    { key: "critical", label: "Critical", count: 0, pct: 0 },
  ];
}

export default function AdminAnalyticsDashboard() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [autoRefresh, setAutoRefresh] = useState(true);
  const pollRef = useRef<number | null>(null);

  const [country, setCountry] = useState("");
  const [region, setRegion] = useState("");
  const [district, setDistrict] = useState("");
  const [school, setSchool] = useState("");
  const [audience, setAudience] = useState<AudienceScope>("all");

  const [countryOptions, setCountryOptions] = useState<FilterOption[]>([]);
  const [regionOptions, setRegionOptions] = useState<FilterOption[]>([]);
  const [districtOptions, setDistrictOptions] = useState<FilterOption[]>([]);
  const [schoolOptions, setSchoolOptions] = useState<FilterOption[]>([]);

  const [publicCounts, setPublicCounts] = useState<PublicCounts>({
    students: 0,
    advisors: 0,
    organizations: 0,
    parents: 0,
    teachers: 0,
    contacts: 0,
    tipsTotal: 0,
    tipsApproved: 0,
    studentsOnline: 0,
    advisorsOnline: 0,
    flaggedInteractions: 0,
    usageEvents: 0,
    advisorRequests: 0,
    translationSessions: 0,
    closureEvents: 0,
  });

  const [studentGroups, setStudentGroups] = useState<Array<{ key: string; count: number }>>([]);
  const [advisorGroups, setAdvisorGroups] = useState<Array<{ key: string; count: number }>>([]);
  const [feed, setFeed] = useState<AuraFeedItem[]>([]);
  const [auraSummary, setAuraSummary] = useState<AuraSummary>({});
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");

  const query = useMemo(
    () =>
      buildQuery({
        days,
        country,
        region,
        district,
        school,
        limit: 200,
      }),
    [days, country, region, district, school]
  );

  function buildReportQuery(reportDays: number) {
    return buildQuery({
      days: reportDays,
      country,
      region,
      district,
      school,
    });
  }

  async function openAuraReport(kind: "csv" | "html", reportDays: number, filenameBase: string) {
    const token = getAdminToken();

    if (!token) {
      setErr("Admin login token was not found. Please log in again before generating reports.");
      return;
    }

    const path = `/admin/aura/report.${kind}${buildReportQuery(reportDays)}`;

    try {
      setErr("");
      const res = await fetch(`${API_BASE}${path}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        const text = await res.text();
        let message = text || `Report request failed: ${res.status}`;
        try {
          const parsed = JSON.parse(text);
          message = parsed?.detail || parsed?.message || message;
        } catch {}
        throw new Error(message);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      if (kind === "html") {
        const win = window.open(url, "_blank", "noopener,noreferrer");
        if (!win) {
          const a = document.createElement("a");
          a.href = url;
          a.download = `${filenameBase}.html`;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
        window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
        return;
      }

      const a = document.createElement("a");
      a.href = url;
      a.download = `${filenameBase}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (e: any) {
      setErr(e?.message || "Unable to generate AURA report.");
    }
  }

  const filteredFeed = useMemo(
    () => feed.filter((item) => inAudienceScope(item, audience)),
    [feed, audience]
  );

  const categoryRowsLive = useMemo(
    () =>
      rankRows(
        filteredFeed.map((item) =>
          formatCategory(
            item.category ||
              item.signal_category ||
              item.trigger ||
              item.signal_label
          )
        ),
        categoryFallback()
      ).slice(0, 20),
    [filteredFeed]
  );

  const severityRowsLive = useMemo(
    () =>
      rankRows(
        filteredFeed.map((item) => severityLabel(item.severity)),
        severityFallback()
      ),
    [filteredFeed]
  );

  const triggerRows = useMemo(
    () =>
      rankRows(
        filteredFeed.map((item) =>
          formatCategory(item.trigger || item.signal_label || item.category)
        )
      ).slice(0, 20),
    [filteredFeed]
  );

  const audienceRows = useMemo(
    () =>
      rankRows(
        feed.map((item) => {
          const role = normalizeAudience(item.audience || item.user_role);
          if (role === "students") return "Students";
          if (role === "parents") return "Parents";
          if (role === "teachers") return "Teachers";
          if (role === "advisors") return "Advisors";
          return "Other / Unknown";
        })
      ),
    [feed]
  );

  const totalFlagged = filteredFeed.length || safeNum(auraSummary.active_signals);

  const normalizedSeverityRows = useMemo(() => {
    const base = severityRowsLive.length ? severityRowsLive : severityFallback();
    const total = base.reduce((sum, row) => sum + row.count, 0);
    return base.map((row) => ({ ...row, pct: pct(row.count, total || totalFlagged) }));
  }, [severityRowsLive, totalFlagged]);

  const emergingRisks = useMemo(() => {
    const priority = [
      "Digital Influence / Viral Challenge",
      "Medication Misuse",
      "Sexting / Image Sharing",
      "Grooming / Sextortion",
      "Suicide Loss / Grief",
      "Self-Harm / Suicide Risk",
      "Violence / Weapon Threat",
      "Eating Disorder / Body Image",
    ];

    const live = categoryRowsLive.filter((row) => priority.includes(row.label));
    return (live.length ? live : categoryFallback()).slice(0, 6);
  }, [categoryRowsLive]);

  const caseyUsageRows = useMemo<RankRow[]>(() => {
    const total = filteredFeed.length;
    const advisorRequests = filteredFeed.filter((item) =>
      formatCategory(item.category || item.trigger).toLowerCase().includes("advisor")
    ).length;
    const closures = filteredFeed.filter((item) =>
      formatCategory(item.category || item.trigger).toLowerCase().includes("closure")
    ).length;
    const digital = filteredFeed.filter((item) =>
      formatCategory(item.category || item.trigger).toLowerCase().includes("digital")
    ).length;
    const highRisk = filteredFeed.filter((item) =>
      ["Critical", "High"].includes(severityLabel(item.severity))
    ).length;

    const rows = [
      { key: "ai_conversations", label: "AI Conversations Logged", count: total, pct: 100 },
      { key: "high_risk", label: "High / Critical Signals", count: highRisk, pct: pct(highRisk, total) },
      { key: "advisor_requests", label: "Advisor Requests", count: advisorRequests, pct: pct(advisorRequests, total) },
      { key: "closure_followup", label: "Closure / Follow-Up", count: closures, pct: pct(closures, total) },
      { key: "digital_safety", label: "Digital Safety Conversations", count: digital, pct: pct(digital, total) },
    ];

    return rows;
  }, [filteredFeed]);

  const supportTitle =
    audience === "students"
      ? "Student Casey Topics"
      : audience === "parents"
        ? "Parent Casey Topics"
        : audience === "teachers"
          ? "Teacher Casey Topics"
          : audience === "advisors"
            ? "Advisor Casey Topics"
            : "Top Casey / AURA Topics";

  const overviewCards = [
    { label: "Students", value: publicCounts.students },
    { label: "Advisors", value: publicCounts.advisors },
    { label: "Parents / Teachers", value: publicCounts.parents + publicCounts.teachers },
    { label: "Organizations", value: publicCounts.organizations },
    { label: "Support Signals", value: publicCounts.flaggedInteractions },
    { label: "Flagged Schools", value: safeNum(auraSummary.flagged_schools) },
    { label: "Wellness", value: `${safeNum(auraSummary.wellness_engagement)}%` },
    { label: "Crisis Watch", value: cleanLabel(auraSummary.crisis_watch) || "Low" },
    { label: "Response", value: `${safeNum(auraSummary.advisor_response)}%` },
  ];

  async function loadFilters() {
    try {
      const [filters, schools] = await Promise.all([
        apiFetch<any>("/admin/analytics/filters"),
        apiFetch<any>(`/admin/aura/schools${buildQuery({ days, country, region, district, limit: 1000 })}`),
      ]);

      setCountryOptions(normalizeOptions(filters?.countries || [], ["value", "name"], ["label", "value", "name"]));
      setRegionOptions(normalizeOptions(filters?.regions || [], ["value", "name"], ["label", "value", "name"]));
      setDistrictOptions(normalizeOptions(filters?.districts || [], ["value", "name"], ["label", "value", "name"]));

      const schoolList = [
        ...(Array.isArray(filters?.schools) ? filters.schools : []),
        ...(Array.isArray(schools?.items) ? schools.items : Array.isArray(schools?.schools) ? schools.schools : []),
      ];

      setSchoolOptions(
        normalizeOptions(
          schoolList,
          ["value", "school_name", "name", "org_name"],
          ["label", "school_name", "name", "org_name"]
        )
      );
    } catch (e) {
      console.warn("[AAD2] filter load failed", e);
      setCountryOptions([]);
      setRegionOptions([]);
      setDistrictOptions([]);
      setSchoolOptions([]);
    }
  }

  async function loadPublicCounts(summary?: AuraSummary) {
    const [adminSummary, studentPresence, advisorPresence, realtimePresence, publicDoctors] = await Promise.all([
      apiFetch<AdminSummary>("/admin/analytics/summary").catch(() => ({} as AdminSummary)),
      apiFetch<any>("/admin/analytics/realtime/students").catch(() => ({ groups: [] })),
      apiFetch<any>("/admin/analytics/realtime/advisors").catch(() => ({ groups: [] })),
      apiFetch<any>("/realtime/presence").catch(() => null),
      apiFetch<any>("/public/doctors").catch(() => null),
    ]);

    const totals = adminSummary?.totals || {};
    const auraTotals = summary || auraSummary || {};
    const online = auraTotals.online || {};

    const studentGroupsRaw = Array.isArray(studentPresence?.groups) ? studentPresence.groups : [];
    const advisorGroupsRaw = Array.isArray(advisorPresence?.groups) ? advisorPresence.groups : [];
    const realtime = summarizeRealtimePresence(realtimePresence, publicDoctors);

    const normalizedStudentGroups = studentGroupsRaw
      .map((g: any) => ({ key: cleanLabel(g.key) || "Unknown", count: safeNum(g.count) }))
      .filter((g: any) => g.count > 0);

    const normalizedAdvisorGroups = advisorGroupsRaw
      .map((g: any) => ({ key: cleanLabel(g.key) || "Unknown", count: safeNum(g.count) }))
      .filter((g: any) => g.count > 0);

    setStudentGroups(normalizedStudentGroups.length ? normalizedStudentGroups : realtime.studentGroups);
    setAdvisorGroups(normalizedAdvisorGroups.length ? normalizedAdvisorGroups : realtime.advisorGroups);

    const studentsOnline =
      safeNum(online.students) ||
      safeNum((realtimePresence || {})?.students_online) ||
      safeNum((realtimePresence || {})?.student_count) ||
      realtime.studentsOnline;

    const advisorsOnline =
      safeNum(online.advisors) ||
      safeNum(online.doctors) ||
      safeNum((realtimePresence || {})?.advisors_online) ||
      safeNum((realtimePresence || {})?.doctors_online) ||
      safeNum((realtimePresence || {})?.advisor_count) ||
      realtime.advisorsOnline;

    setPublicCounts((prev) => ({
      ...prev,
      students: safeNum(totals.students),
      advisors: safeNum(totals.doctors),
      organizations: safeNum(totals.organizations),
      parents: 0,
      teachers: safeNum(totals.parent_teachers),
      contacts: safeNum(totals.contacts),
      tipsTotal: prev.tipsTotal,
      tipsApproved: prev.tipsApproved,
      studentsOnline,
      advisorsOnline,
      flaggedInteractions: safeNum(auraTotals.active_signals) || safeNum(totals.support_signal_events),
      usageEvents: safeNum(totals.support_signal_events),
      advisorRequests: filteredFeed.filter((item) =>
        formatCategory(item.category || item.trigger).toLowerCase().includes("advisor")
      ).length,
      translationSessions: 0,
      closureEvents: filteredFeed.filter((item) =>
        formatCategory(item.category || item.trigger).toLowerCase().includes("closure")
      ).length,
    }));

    setLastUpdatedAt(new Date().toLocaleString());
  }

  async function loadAuraData() {
    const [summary, feedData] = await Promise.all([
      apiFetch<AuraSummary>(`/admin/aura/summary${query}`),
      apiFetch<any>(`/admin/aura/feed${query}`),
    ]);

    setAuraSummary(summary || {});

    const items = Array.isArray(feedData?.items) ? feedData.items : [];
    setFeed(items);

    await loadPublicCounts(summary || {});
  }

  async function refreshAll() {
    setErr("");
    setLoading(true);

    try {
      await Promise.all([loadFilters(), loadAuraData()]);
    } catch (e: any) {
      setErr(e?.message || "Analytics refresh failed.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, audience, country, region, district, school]);

  useEffect(() => {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }

    if (!autoRefresh) return;

    pollRef.current = window.setInterval(() => {
      loadAuraData().catch(() => {});
    }, 7000);

    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, query, audience]);

  return (
    <div
      className="aad-page"
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "0 28px 20px",
        boxSizing: "border-box",
      }}
    >
      <div
        className="aad-sheet"
        style={{
          width: "100%",
          maxWidth: "1760px",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        <header className="aad-topbar">
          <div className="aad-brand">
            <img className="aad-logo" src={logo} alt="Doctor Is In" />
            <div>
              <div className="aad-title">AURA Analytics 2.0</div>
              <div className="aad-subtitle">
                Casey intelligence, support signals, and school wellness analytics
              </div>
            </div>
          </div>

          <div className="aad-actions">
            <button className="aad-btn" onClick={refreshAll} disabled={loading}>
              {loading ? "Refreshing..." : "Refresh"}
            </button>


            <button
              className="aad-btn"
              onClick={() => openAuraReport("csv", 90, "aura-quarterly-report")}
            >
              Quarterly CSV
            </button>

            <button
              className="aad-btn"
              onClick={() => openAuraReport("csv", 365, "aura-year-end-report")}
            >
              Year-End CSV
            </button>

            <button
              className="aad-btn"
              onClick={() => openAuraReport("html", 90, "aura-quarterly-report")}
            >
              Quarterly Report
            </button>

            <button
              className="aad-btn"
              onClick={() => openAuraReport("html", 365, "aura-year-end-report")}
            >
              Year-End Report
            </button>

            <label className="aad-toggle">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>

            <div className="aad-days">
              <span>Days:</span>
              <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10) || 30)}>
                <option value={7}>7</option>
                <option value={14}>14</option>
                <option value={30}>30</option>
                <option value={60}>60</option>
                <option value={90}>90</option>
                <option value={180}>180</option>
                <option value={365}>365</option>
              </select>
            </div>
          </div>
        </header>

        {err ? <div className="aad-error">{err}</div> : null}

        <section className="aad-card aad-filters-card">
          <div className="aad-card-h">Scope Filters</div>

          <div className="aad-filter-row">
            <label className="aad-filter">
              <span>Audience</span>
              <select value={audience} onChange={(e) => setAudience(e.target.value as AudienceScope)}>
                <option value="all">All Groups</option>
                <option value="students">Students</option>
                <option value="parents">Parents</option>
                <option value="teachers">Teachers</option>
                <option value="advisors">Advisors</option>
              </select>
            </label>

            <label className="aad-filter">
              <span>Country</span>
              <select value={country} onChange={(e) => setCountry(e.target.value)}>
                <option value="">All Countries</option>
                {countryOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>

            <label className="aad-filter">
              <span>Region</span>
              <select value={region} onChange={(e) => setRegion(e.target.value)}>
                <option value="">All Regions</option>
                {regionOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>

            <label className="aad-filter">
              <span>District</span>
              <select value={district} onChange={(e) => setDistrict(e.target.value)}>
                <option value="">All Districts</option>
                {districtOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>

            <label className="aad-filter">
              <span>School</span>
              <select value={school} onChange={(e) => setSchool(e.target.value)}>
                <option value="">All Schools</option>
                {schoolOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="aad-muted">
            Filter by audience, location, district, or school. Data now comes from FastAPI AURA endpoints, not legacy Supabase views.
          </div>
        </section>

        <section className="aad-overview-grid">
          {overviewCards.map((card) => (
            <div className="aad-stat-card" key={card.label}>
              <div className="aad-stat-label">{card.label}</div>
              <div className="aad-stat-value">{card.value}</div>
            </div>
          ))}
        </section>

        <section className="aad-grid">
          <div className="aad-card">
            <div className="aad-card-h">Realtime Status</div>
            <div className="aad-metrics">
              <div className="aad-metric">
                <div className="aad-metric-k">Students online</div>
                <div className="aad-metric-v">{publicCounts.studentsOnline}</div>
              </div>
              <div className="aad-metric">
                <div className="aad-metric-k">Advisors online</div>
                <div className="aad-metric-v">{publicCounts.advisorsOnline}</div>
              </div>
              <div className="aad-metric">
                <div className="aad-metric-k">Support signals</div>
                <div className="aad-metric-v">{totalFlagged}</div>
              </div>
            </div>

            <div className="aad-muted">Last update: {lastUpdatedAt || "—"}</div>
          </div>

          <div className="aad-card">
            <div className="aad-card-h">Casey Usage Snapshot</div>
            <div className="aad-kv aad-kv-2col">
              <div><b>Logged conversations:</b> {caseyUsageRows[0]?.count || 0}</div>
              <div><b>High / critical:</b> {caseyUsageRows[1]?.count || 0}</div>
              <div><b>Advisor requests:</b> {caseyUsageRows[2]?.count || 0}</div>
              <div><b>Digital safety:</b> {caseyUsageRows[4]?.count || 0}</div>
            </div>
          </div>
        </section>

        <section className="aad-grid2">
          <div className="aad-card">
            <div className="aad-card-h">Students Active by Grade</div>
            {studentGroups.length ? (
              <table className="aad-table">
                <thead>
                  <tr>
                    <th>Grade</th>
                    <th style={{ textAlign: "right" }}>Active</th>
                  </tr>
                </thead>
                <tbody>
                  {studentGroups.map((g) => (
                    <tr key={g.key}>
                      <td>{g.key}</td>
                      <td style={{ textAlign: "right" }}>{g.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="aad-empty">No student activity detected in the current realtime window.</div>
            )}
          </div>

          <div className="aad-card">
            <div className="aad-card-h">Advisors Online by Profession</div>
            {advisorGroups.length ? (
              <table className="aad-table">
                <thead>
                  <tr>
                    <th>Profession</th>
                    <th style={{ textAlign: "right" }}>Online</th>
                  </tr>
                </thead>
                <tbody>
                  {advisorGroups.map((g) => (
                    <tr key={g.key}>
                      <td>{g.key}</td>
                      <td style={{ textAlign: "right" }}>{g.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="aad-empty">No advisor presence detected in the current realtime window.</div>
            )}
          </div>
        </section>

        <section className="aad-grid2">
          <div className="aad-card">
            <div className="aad-card-h">{supportTitle}</div>

            <div className="aad-flagged-total">
              <span className="aad-flagged-label">Total support signals</span>
              <span className="aad-flagged-value">{totalFlagged}</span>
            </div>

            <table className="aad-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th style={{ textAlign: "right" }}>Count</th>
                  <th style={{ textAlign: "right" }}>%</th>
                </tr>
              </thead>
              <tbody>
                {categoryRowsLive.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td style={{ textAlign: "right" }}>{row.count}</td>
                    <td style={{ textAlign: "right" }}>{row.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="aad-muted">
              Categories use the new AURA taxonomy from Casey support signals.
            </div>
          </div>

          <div className="aad-card">
            <div className="aad-card-h">Severity Mix</div>

            <table className="aad-table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th style={{ textAlign: "right" }}>Count</th>
                  <th style={{ textAlign: "right" }}>%</th>
                </tr>
              </thead>
              <tbody>
                {normalizedSeverityRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td style={{ textAlign: "right" }}>{row.count}</td>
                    <td style={{ textAlign: "right" }}>{row.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="aad-muted">
              Severity reporting supports low, moderate, high, and critical escalation visibility.
            </div>
          </div>
        </section>

        <section className="aad-grid2">
          <div className="aad-card">
            <div className="aad-card-h">Emerging Risks</div>

            <table className="aad-table">
              <thead>
                <tr>
                  <th>Risk Area</th>
                  <th style={{ textAlign: "right" }}>Signals</th>
                  <th style={{ textAlign: "right" }}>%</th>
                </tr>
              </thead>
              <tbody>
                {emergingRisks.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td style={{ textAlign: "right" }}>{row.count}</td>
                    <td style={{ textAlign: "right" }}>{row.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="aad-muted">
              Highlights digital safety, medication misuse, exploitation, grief, and other high-value trends.
            </div>
          </div>

          <div className="aad-card">
            <div className="aad-card-h">Audience Breakdown</div>

            <table className="aad-table">
              <thead>
                <tr>
                  <th>Audience</th>
                  <th style={{ textAlign: "right" }}>Signals</th>
                  <th style={{ textAlign: "right" }}>%</th>
                </tr>
              </thead>
              <tbody>
                {audienceRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td style={{ textAlign: "right" }}>{row.count}</td>
                    <td style={{ textAlign: "right" }}>{row.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="aad-muted">
              Tracks student, parent, and teacher support demand across the selected time window.
            </div>
          </div>
        </section>

        <section className="aad-card">
          <div className="aad-card-h">Top Trigger / Topic Signals</div>

          {triggerRows.length ? (
            <table className="aad-table">
              <thead>
                <tr>
                  <th>Signal</th>
                  <th style={{ textAlign: "right" }}>Count</th>
                  <th style={{ textAlign: "right" }}>%</th>
                </tr>
              </thead>
              <tbody>
                {triggerRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.label}</td>
                    <td style={{ textAlign: "right" }}>{row.count}</td>
                    <td style={{ textAlign: "right" }}>{row.pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="aad-empty">No trigger signal data is available for the selected filters yet.</div>
          )}
        </section>

        <section className="aad-card">
          <div className="aad-card-h">Recent Casey / AURA Signal Feed</div>

          {filteredFeed.length ? (
            <table className="aad-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>School</th>
                  <th>Audience</th>
                  <th>Category</th>
                  <th>Trigger</th>
                  <th style={{ textAlign: "right" }}>Severity</th>
                </tr>
              </thead>
              <tbody>
                {filteredFeed.slice(0, 25).map((item, idx) => (
                  <tr key={`${item.id || idx}-${item.created_at || ""}`}>
                    <td>{cleanLabel(item.created_at) || "—"}</td>
                    <td>{cleanLabel(item.school_name) || "Unknown"}</td>
                    <td>{cleanLabel(item.audience || item.user_role) || "Unknown"}</td>
                    <td>{formatCategory(item.category || item.signal_category)}</td>
                    <td>{formatCategory(item.trigger || item.signal_label)}</td>
                    <td style={{ textAlign: "right" }}>{severityLabel(item.severity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="aad-empty">No recent AURA signal feed is available for the selected filters yet.</div>
          )}
        </section>
      </div>
    </div>
  );
}
